/**
 * User-facing copy for campaign record operations.
 */
export const RECORD_COPY = {
  addRecord: 'Add record',
  editRecord: 'Edit record',
  viewRecord: 'View record',
  deleteRecord: 'Delete record',
  refresh: 'Refresh',
  filters: 'Filters',
  columns: 'Columns',
  saveView: 'Save view',
  columnsMenu: {
    close: 'Close',
    helper: 'Changes apply instantly and are saved for this table.',
    selectAll: 'Show all columns',
    reset: 'Reset',
    visibleCount: (visible, total) => `${visible} of ${total} visible`,
  },
  moreActions: 'More actions',
  actions: {
    createReport: 'Create report',
    download: 'Download records',
    importRecords: 'Import records',
    // enterRecords: 'Enter records',
    editForm: 'Edit form',
    reports: 'Reports',
    help: 'Help',
  },
  download: {
    title: 'Download records',
    appliedFilters: 'Applied filters',
    selectColumns: 'Select columns to download',
    selectAll: 'Select all',
    noFilters: 'No filters applied',
    dateRange: 'Date range',
    search: 'Search',
    dynamicFilters: (count) => `${count} filter${count === 1 ? '' : 's'} applied`,
    confirm: 'Download records',
    cancel: 'Cancel',
  },
  help: {
    title: 'Help',
  },
  // import: {
  //   title: 'Import records',
  //   subtitle: 'Upload from Excel or connect an external lead source with dynamic field mapping.',
  //   downloadSample: 'Download sample file',
  //   uploadHint: 'Drag and drop your file here, or click to browse',
  //   uploadFormats: 'Supports .xlsx only',
  //   replaceFile: 'Choose a different file',
  //   uploadRecords: 'Upload records',
  //   uploading: 'Uploading…',
  //   downloadErrorFile: 'Download error file',
  //   importOtherSources: 'Import from other sources',
  //   back: 'Back to records',
  //   errors: {
  //     noFile: 'Please select a file to upload.',
  //     invalidType: 'Please upload a valid .xlsx file.',
  //     missingForm: 'Form ID is missing. Return to the records table and try again.',
  //     uploadFailed: 'Failed to upload records. Please try again.',
  //     uploadError: 'Something went wrong during upload. Please try again.',
  //     noErrorData: 'No error data available to download.',
  //     errorDownloadFailed: 'Failed to download the error file.',
  //   },
  //   success: {
  //     uploaded: 'Records imported successfully.',
  //     errorFileDownloaded: 'Error file downloaded.',
  //   },
  // },
  reports: {
    title: 'Reports',
  },
  rowActions: {
    addComment: 'Add comment',
    manageRecord: 'Manage record',
    formDetail: 'Form detail',
    statusLogs: 'Status logs',
    sendTemplate: 'Send template',
    images: 'Images',
    quickCall: 'Quick call',
    audioSummary: 'Audio summary',
    chat: 'Chat',
    assignLead: 'Assign lead',
  },
  columnCells: {
    audioView: 'View',
    audioUpload: 'Upload',
    aiChatAction: 'Chat',
  },
  comments: {
    title: 'Comments',
    subtitle: (leadId) => (leadId ? `Lead ${leadId}` : 'Record notes and activity'),
    count: (count) => `${count} comment${count === 1 ? '' : 's'}`,
    emptyTitle: 'No comments yet',
    emptyDescription: 'Add the first note for this record. Your team will see it here.',
    noCommentPreview: 'No comment yet',
    placeholder: 'Write a comment…',
    send: 'Add comment',
    close: 'Close',
  },
  bulk: {
    selected: (count) => `${count} selected`,
    clear: 'Clear selection',
    delete: 'Delete',
    updateStatus: 'Update status',
    sendTemplate: 'Send template',
    assign: 'Assign',
    moveTo: 'Move to',
    moveTitle: 'Move records',
    moveSubtitle: 'Select the target campaign and map fields before moving selected records.',
    moveConfirm: 'Move records',
    currentCampaign: 'Current campaign',
    currentForm: 'Current form',
    targetCampaign: 'Target campaign',
    targetForm: 'Target form',
    mapField: 'Map',
    statusSubtitle: 'Choose status values to apply to all selected records.',
    noStatusFields: 'No status fields are configured for this form.',
  },
  drawer: {
    createTitle: 'Create record',
    createSubtitle: 'Complete the form below to add a new record.',
    editTitle: 'Edit record',
    viewTitle: 'Record details',
    save: 'Save',
    edit: 'Edit',
    cancel: 'Cancel',
  },
  deleteConfirm: {
    title: 'Delete record?',
    titleBulk: 'Delete selected records?',
    message: 'Are you sure you want to delete this record? This action cannot be undone.',
    confirmSingle: 'Yes',
    confirm: 'Delete',
    cancel: 'Cancel',
  },
  errors: {
    loadRecord: 'Unable to load record',
    saveFailed: 'Failed to save record',
    deleteFailed: 'Failed to delete record',
    downloadFailed: 'Failed to download records',
    commentFailed: 'Failed to add comment',
    assignFailed: 'Failed to assign leads',
    unassignFailed: 'Failed to unassign leads',
    phoneRequired: 'Phone number is required',
    phoneUnavailable: 'Phone number not available for this record.',
  },
  success: {
    saved: 'Record saved successfully',
    deleted: 'Record deleted successfully',
    viewSaved: 'Column view saved',
    statusUpdated: 'Status updated successfully',
    entityUpdated: 'Entity updated successfully',
    moved: 'Records moved successfully',
    downloadStarted: 'Download started',
    commentAdded: 'Comment added successfully',
    assigned: 'Leads assigned successfully',
  },
  inlineStatusConfirm: {
    confirm: 'Yes',
    cancel: 'No',
  },
  empty: {
    noFilters: 'No filters applied',
  },
  assign: {
    title: 'Assign lead',
    subtitle: (count) => (
      count > 1
        ? `Assign or unassign users for ${count} selected records.`
        : 'Assign or unassign users for this record.'
    ),
    currentlyAssigned: 'Currently assigned',
    selectUsers: 'Select users',
    removeAssignee: 'Remove assignee',
    noUsers: 'No users available for this campaign',
    noLeads: 'Lead id not found',
    noCampaign: 'Campaign is required',
    noChanges: 'No changes detected',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'All changes saved successfully',
    partialSaved: 'Some assignment changes saved',
    saveFailed: 'Failed to save assignment changes',
    assignFailed: 'Failed to assign users',
    unassignFailed: 'Failed to unassign users',
  },
  sendTemplate: {
    title: 'Send WhatsApp template',
    cancel: 'Cancel',
    close: 'Close',
    noPhone: 'No phone',
    templateLoadFailed: 'Failed to load template details',
    selectTemplateFirst: 'Select a bot number and template first',
    noRecipients: 'No recipients selected',
    sendFailed: 'Failed to send template',
    noSheetFile: 'Upload a filled Excel sheet first',
    sheetInvalid: 'Fix sheet errors before sending',
    uploadFailed: 'Failed to upload recipients',
    uploadSuccess: 'Recipients uploaded — template send queued',
    noErrorSheet: 'No validation errors to export',
    summary: (sent, failed, skipped) =>
      `Sent ${sent}, failed ${failed}, skipped ${skipped}`,
    subtitleSingle: (leadId, name, phone) => {
      const parts = [leadId ? `Lead ${leadId}` : null, name || null, phone].filter(Boolean);
      return parts.join(' · ');
    },
    subtitleMulti: (total, withPhone, withoutPhone) => {
      const base = `${total} record${total === 1 ? '' : 's'} · ${withPhone} with phone`;
      return withoutPhone ? `${base} · ${withoutPhone} without phone` : base;
    },
    botLabel: 'Bot number',
    templateLabel: 'Template',
    quickTemplates: 'Quick templates',
    tabs: {
      direct: 'Send now',
      sheet: 'Upload sheet',
    },
    recommended: 'Recommended',
    coverage: {
      title: 'Parameter coverage',
      mapped: 'Mapped',
      partial: 'Partial',
      missing: 'Missing',
      agent: 'Agent',
      fillViaSheet: 'Fill via sheet',
      mapManually: 'Map manually below',
      rowFill: (filled, total) => `${filled}/${total} rows filled`,
    },
    bulk: {
      sendToCount: (count) => `Send to ${count} record${count === 1 ? '' : 's'}`,
      readyTitle: (count) => `${count} record${count === 1 ? '' : 's'} selected`,
      readyHint: 'Map columns, download the sheet to verify, then upload to send.',
      allMapped: 'All parameters are mapped from the table.',
      mapColumnsTitle: 'Map variables',
      mapColumnsHint: 'Choose a table column for each template variable. Applies to all selected records.',
      selectColumn: 'Select column',
      agentParamHint: 'Auto-filled from your profile',
      verifySheetTitle: 'Verify & send',
      verifySheetHint: 'Download the mapped sheet, verify values, upload the verified file, then send.',
      downloadMapped: 'Download mapped sheet',
      sheetTitle: (count) => `Bulk send · ${count} records`,
      sheetHint: 'Some parameters could not be mapped from the table. Use a pre-filled sheet to complete and send.',
      gapLine: (label, missingCount, totalRows) =>
        `"${label}" missing on ${missingCount} of ${totalRows} records`,
    },
    direct: {
      sendNow: 'Send now',
      fillMessageTitle: 'Fill message',
      fillMessageHint: 'Complete each variable below or map it from a table column.',
      paramLabel: (index, label) => `{{${index}}} ${label}`,
      noParamsRequired: 'This template has no body variables to fill.',
      fromRecordHint: 'Mapped from this record',
      multiNote:
        'Each selected record will receive the template with parameters mapped from its own row.',
      singlePartialHint:
        'Some parameters were not auto-mapped. Fill them below or pick a table column.',
      partialWarning:
        'Some parameters are missing or partially filled for one or more records. Use Upload sheet to fill gaps for bulk send.',
      noPhoneWarning: 'No selected records have a valid phone number.',
      progress: (done, total) => `Sending ${done} of ${total}…`,
      previewTitle: 'Parameter preview (first record)',
      editParamsTitle: 'Template parameters',
      mapFromColumn: 'Map from column',
      mapColumnNone: 'Enter manually',
      paramValue: 'Value',
      paramValuePlaceholder: 'Enter value for this parameter',
      paramRequired: 'This field is required',
      mapManuallyHint: 'Enter a value or pick a column above',
      autoMappedHint: (value) => `Auto-mapped value: ${value}`,
      fillParamsFirst: 'Fill all template parameters before sending',
      multiMissingBlocked: 'Some records are missing parameters — use Upload sheet for bulk send',
      headerSectionTitle: (format) => `Header ${(format || '').toLowerCase()}`,
      headerUploadHintImage: 'Allowed: JPG, PNG • Max 5 MB',
      headerUploadHintVideo: 'Allowed: MP4, 3GP • Max 16 MB',
      headerUploadHintDocument: 'Allowed: PDF, DOC, DOCX• Max 95 MB',
      useTemplateHeaderLabel: (format) =>
        `Use the template's uploaded ${(format || 'media').toLowerCase()} header`,
      headerRequired: 'Please upload a header file or use the template\'s uploaded header.',
      headerUploadFailed: 'Failed to upload header file',
    },
    sheet: {
      downloadPrefilled: 'Download pre-filled sample',
      downloadBlank: 'Download blank template sample',
      uploadHint: 'Drop your filled Excel file here, or click to browse',
      replaceFile: 'Replace file',
      uploadAndSend: 'Upload & send',
      downloadErrorSheet: 'Download error sheet',
      validationFailed: (count) =>
        `${count} row${count === 1 ? '' : 's'} have errors — fix and re-upload`,
      rowErrorPreview: (rowNumber, summary) => `Row ${rowNumber}: ${summary}`,
      uploadProgress: (percent) => `Uploading ${percent}%…`,
    },
    empty: {
      noBot: 'Select a bot number to load templates',
      noBotDescription: 'Choose the WhatsApp bot that will send this template.',
      noBots: 'No WhatsApp number configured',
      noBotsDescription: 'No WhatsApp number configured for this organisation.',
      noTemplates: 'No approved templates found',
      noTemplatesDescription: 'Configure templates in Form Studio assets or WhatsApp templates.',
    },
    errorSheetGuide: [
      'Required columns: phone_number plus all template body parameters.',
      'Phone must be 10 digits or include country code (e.g. 919876543210).',
      'Remove error_summary and error_fields columns before re-uploading, or use the original column layout.',
    ],
  },
  statusLogs: {
    title: 'Status Logs',
    subtitle: 'Status change history for this record',
    // payloadColumns: 'Select payload columns',
    payloadColumnsEmpty: 'No payload fields available',
    empty: 'No status logs found',
    loadError: 'Failed to fetch status logs',
    backToLogs: 'Back to status logs',
    selectDateFirst: 'Please select a date first.',
    templateIdMissing: (name) => `Template ID not found for "${name}".`,
    columns: {
      status: 'Status',
      subStatus: 'Sub status',
      statusType: 'Status type',
      template: 'Template',
      triggeredBy: 'Triggered by',
      triggeredOn: 'Triggered on',
      responseFrom: 'Response from',
      responseTime: 'Response time',
      comments: 'Comments',
      payloadPrefix: 'Payload: ',
    },
    template: {
      loadDatesError: 'Failed to fetch template date-wise summary.',
      loadUsersError: 'Failed to fetch template users.',
      idMissing: 'Template ID is missing for this template.',
      dateMissing: 'Date not available in summary.',
      filterDateRange: 'Filter by date range',
      startDate: 'Start date',
      endDate: 'End date',
      clearRange: 'Clear',
      loadingDates: 'Loading date-wise template summary…',
      loadingUsers: 'Loading template users…',
      noDates: 'No date-wise summary found.',
      noUsers: 'No users found for the selected date.',
      tabs: {
        details: 'Template details',
        dates: 'Date summary',
        users: 'Users',
      },
      columns: {
        date: 'Date',
        totalUsers: 'Total users',
        delivered: 'Delivered',
        read: 'Read',
        clicked: 'Clicked',
        failed: 'Failed',
        userName: 'User name',
        whatsappName: 'WhatsApp name',
        phoneNumber: 'Phone number',
        templateStatus: 'Template status',
        updatedAt: 'Updated at',
        assignedTo: 'Assigned to',
        agentStatus: 'Agent / call status',
        comments: 'Comments',
      },
      fields: {
        templateId: 'Template ID',
        name: 'Name',
        status: 'Status',
        sentCreated: 'Sent / created',
      },
      statusHistory: 'Status history for this template',
    },
  },
};
